import { fg } from '@atlaskit/platform-feature-flags/fg';

import { NLPSearchErrorState } from '../../../ai-answer-dialog/nlp-search-error-state';
import { NLPSearchResultFormat } from '../../../ai-answer-dialog/nlp-search-result-format';
import type { SearchAIAnswerQueryType, TraceHeaders } from '../../../ai-answer-dialog/types';
import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import { logger } from '../../../common/utils/logger';
import {
	type KnowledgeDiscoveryTopicByIdData,
	type KnowledgeDiscoveryTopicByIdVariables,
	TOPIC_BY_ID_OPERATION_NAME,
	TopicByIdQuery,
} from './gql';

type fetchTopicCardProps = {
	cloudId: string;
	query: string;
	workspaceId: string;
	sourceProduct?: string;
	traceHeaders?: TraceHeaders;
};

const getTopicId = (topicIds: string) => {
	const list = JSON.parse(topicIds);
	if (Array.isArray(list) && list.length) {
		return list[0];
	}

	return '';
};

const NO_TOPIC_FOUND_MESSAGE = 'No topic found for id:';

export const fetchTopicCardData = ({
	cloudId,
	query,
	workspaceId,
	sourceProduct,
	traceHeaders,
}: fetchTopicCardProps): Promise<SearchAIAnswerQueryType> => {
	let responseTraceId: string | undefined;
	let responseRequestId: string | undefined;
	let responseError: Error | undefined;
	const userCount = 5;

	// transformedQuery from intent detection passed as query prop to SearchAIDialog from SearchPage
	// transformedQuery is a str e.g. "["f6aac8a1-7907-4fb0-88ca-d7a564b45db3"]" and need to extract id
	const topicId = getTopicId(query);
	return fetchAggResponse<KnowledgeDiscoveryTopicByIdVariables, KnowledgeDiscoveryTopicByIdData>({
		graphQuery: TopicByIdQuery,
		variables: {
			cloudId,
			topicId,
			userCount,
		},
		operationName: TOPIC_BY_ID_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId,
				product: sourceProduct ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	})
		.then(({ data, errors, gatewayTracingHeaders }) => {
			responseTraceId = gatewayTracingHeaders?.['x-trace-id'];
			responseRequestId = gatewayTracingHeaders?.['atl-request-id'];
			const message = data?.knowledgeDiscovery.topic.message;
			// if message includes no topic found, we move forward to rendering card
			// in no topic found state (right now is nothing similar to People/Team card)
			if (errors || (message && !message.includes(NO_TOPIC_FOUND_MESSAGE))) {
				responseError = new Error(JSON.stringify({ errors, message }));
				throw responseError;
			}

			const topicUserData = data?.knowledgeDiscovery || {};

			return {
				nlpSearch: {
					nlpResult: JSON.stringify(topicUserData),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
				},
			};
		})
		.catch((error) => {
			if (fg('kd_search_ai_robust_sentry_logs') || fg('kd_enable_networkerror_logging')) {
				logger.error({
					feature: 'knowledge-cards',
					element: 'container',
					operation: 'fetch',
					category: responseError && error === responseError ? 'graphql' : 'unknown',
					message: 'Knowledge card request failed',
					traceId: responseTraceId ?? traceHeaders?.['X-B3-TraceId'],
					requestId: responseRequestId,
					error: responseError && error === responseError ? undefined : error,
					legacyError: error,
					functionName: 'fetchTopicCardData',
				});
			}
			return Promise.resolve({
				nlpSearch: {
					errorState: NLPSearchErrorState.NetworkError,
					disclaimer: null,
					format: NLPSearchResultFormat.JSON,
					uniqueSources: [],
				},
			});
		});
};
